import { useLoading } from '@genial/components';
import {
  instanceOfErrorListResponse,
  type BasketOrderStatusEntity,
  type ErrorListResponse,
} from '@genial/services';
import { Utils } from '@genial/utils';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import React, { createContext, useContext, useState } from 'react';

import { useToastCreator } from '../../../hooks/useToastCreator';
import type {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../routes/types';
import {
  cancelAdvisorBasketService,
  patchBasketAdvisorByAccepted,
} from '../../BatchTicket/services';
import {
  errorSendRedeemOffersMessage,
  errorCancelRedeemOfferMessage,
  cancelingRedeemOffersLoadingMessage,
  sendingRedeemOffersLoadingMessage,
  sendingRedeemOffersToastMessageSuccess,
  cancelingRedeemOffersToastMessageSuccess,
} from '../utils/constants';

import { useRedeemOffersContext } from './redeem-offers';
import { useRedeemRowsContext } from './redeem-rows';
import { useRedeemSummaryContext } from './redeem-summary';

interface RedeemDetailsContextType {
  lastUpdatedDate: string;
  handleRefreshRedeemDetails: () => Promise<void>;
  basketAdvisorStatus: BasketOrderStatusEntity;
  basketAdvisorId: string;
  handleSendRedeemOffers: () => Promise<void>;
  handleCancelRedeemOffers: () => Promise<void>;
  redeemOffersStatus: RedeemOffersStatusType;
}

type RedeemOffersStatusType = 'review' | 'sent';

const RedeemDetailsContext = createContext<RedeemDetailsContextType>(
  {} as RedeemDetailsContextType
);

export const RedeemDetailsProvider = ({ children }: { children: React.ReactNode }) => {
  const { redeemSummaryRefresher, redeemRowsSummaryRefresher } = useRedeemSummaryContext();
  const { redeemOffersRefresher } = useRedeemOffersContext();
  const { redeemRowsRefresher } = useRedeemRowsContext();
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'RedeemBatchTicketDetails'>>();

  const basketAdvisorId = params.basketAdvisorId;
  const basketAdvisorStatus = params.basketAdvisorStatus;

  const initialRedeemOffersStatus: RedeemOffersStatusType =
    basketAdvisorStatus.statusId === 1002 ? 'review' : 'sent';

  const [redeemOffersStatus, setRedeemOffersStatus] =
    useState<RedeemOffersStatusType>(initialRedeemOffersStatus);

  const [lastUpdatedDate, setLastUpdatedDate] = useState(
    Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy hh:mm:ss')
  );
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { showToast, showToastFromError } = useToastCreator('toast-redeem-ticket');
  const { hide, show } = useLoading();

  const handleRefreshRedeemDetails = async () => {
    if (redeemOffersStatus === 'sent') {
      redeemOffersRefresher();
      redeemSummaryRefresher();
    } else {
      redeemRowsRefresher();
      redeemRowsSummaryRefresher();
    }
    setLastUpdatedDate(Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy hh:mm:ss'));
  };

  const handleSendRedeemOffers = async () => {
    try {
      show(sendingRedeemOffersLoadingMessage);

      await patchBasketAdvisorByAccepted(params.basketAdvisorId, ['whatsapp']);
      await handleRefreshRedeemDetails();
      showToast(...sendingRedeemOffersToastMessageSuccess);
      navigate('BatchTicketRequests');
      setRedeemOffersStatus('sent');
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorSendRedeemOffersMessage);
    } finally {
      hide();
    }
  };

  const handleCancelRedeemOffers = async () => {
    try {
      show(cancelingRedeemOffersLoadingMessage);
      await cancelAdvisorBasketService(params.basketAdvisorId);
      showToast(...cancelingRedeemOffersToastMessageSuccess);
      navigate('BatchTicketRequests');
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorCancelRedeemOfferMessage);
    } finally {
      hide();
    }
  };

  return (
    <RedeemDetailsContext.Provider
      value={{
        lastUpdatedDate,
        handleRefreshRedeemDetails,
        basketAdvisorStatus,
        basketAdvisorId,
        handleSendRedeemOffers,
        handleCancelRedeemOffers,
        redeemOffersStatus,
      }}>
      {children}
    </RedeemDetailsContext.Provider>
  );
};

export const useRedeemDetailsContext = (): RedeemDetailsContextType => {
  const context = useContext(RedeemDetailsContext);

  if (!context) {
    throw new Error('useRedeemDetailsContext must be used within an RedeemDetailsContext');
  }
  return context;
};
